import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import CompanyApi from '@/api/company-api'
import { TimeUtil } from '@/helpers/TimeUtil'
import ComboApi from '@/api/combo-api';
import axios from 'axios';
import dayjs from 'dayjs';

// Shared by callers that ask while a request is already in flight
let companyInfoRequest: Promise<CompanyInfoDto> | null = null;
// Most recently started request (kept after it finishes) so older responses can detect they are stale
let latestCompanyInfoRequest: Promise<CompanyInfoDto> | null = null;

@Module({ namespaced: true, name: 'company' })
export default class CompanyModule extends VuexModule {
  public company: CompanyInfoDto = {} as CompanyInfoDto;
  public personnel: CompanyPersonnelDto[] = [];
  public isLoadingTable = true;

  // Default coordinates - Athens, Greece
  private latitude = 37.983810;
  private longitude = 23.727539;
  public companyHasCoordinates = false;


  /**
   * Loads the company. Concurrent calls share one in-flight request; pass forceRefresh = true after
   * saving company data so the reload never joins a request that started before the save.
   */
  @Action({ commit: 'setCompanyInfo'})
  async getCompanyInfo (forceRefresh = false): Promise<CompanyInfoDto> {
    if (!companyInfoRequest || forceRefresh) {
      const request: Promise<CompanyInfoDto> = new CompanyApi()
        .getCompanyInfo()
        .finally(() => {
          if (companyInfoRequest === request) {
            companyInfoRequest = null;
          }
        });
      companyInfoRequest = request;
      latestCompanyInfoRequest = request;
    }

    // If a newer request started while we waited (e.g. a forced refresh after a save), use its result,
    // so an older response never overwrites newer data in the store.
    let request = companyInfoRequest as Promise<CompanyInfoDto>;
    let company = await request;
    while (latestCompanyInfoRequest && latestCompanyInfoRequest !== request) {
      request = latestCompanyInfoRequest;
      company = await request;
    }

    // Each commit gets its own copy: setCompanyInfo mutates the object (Athens fallback coordinates),
    // so a shared object would make the next commit treat the fallback as real coordinates.
    // A failed request resolves to undefined (see BaseApi.handleError); pass it through unchanged so
    // the commit fails and the store keeps the previous company, as before.
    return company ? { ...company } : company;
  }

  @Action({ commit: 'setCompanyPersonnel'})
  async getCompanyPersonnel (): Promise<CompanyPersonnelDto[]> {

    const companyApi = new CompanyApi()
    const companyPersonnel = await companyApi.getCompanyPersonnel();
    return companyPersonnel
  }

  @Action
  async getBackUpFile() {
    const companyApi = new CompanyApi()
    const response = await companyApi.getCompanyBackUp(`?user_timezone=${dayjs.tz.guess()}&language=${localStorage.getItem('lang')}`);
    const blob = new Blob([response], { type: "application/zip" });
    return blob
  }

  @Mutation
  public setCompanyInfo (company: CompanyInfoDto): void {
    company.showCompanyMapMarker = !company.latitude && !company.longitude ? false : true;
    company.companyHasCoordinates = company.latitude && company.longitude ? true : false;
    if(!company.latitude && !company.longitude) {
      company.latitude = this.latitude;
      company.longitude = this.longitude;
    }
    this.company = company
  }

  @Mutation
  public setCompanyPersonnel (companyPersonnel: CompanyPersonnelDto[]): void {
    try {
    companyPersonnel.map(item => {
      item.created_at = `${TimeUtil.formatUnixTS(item.createdTimestamp/1000)}`
    })
    this.personnel = companyPersonnel;
    this.isLoadingTable = false;
  } catch(error) {
    this.isLoadingTable = false;
  }
  }

  @Mutation
  public setIsLoadingTable(isLoadingTable: boolean): void {
    this.isLoadingTable = isLoadingTable
  }

  @Action
  async editCompany(data: Record<string, any>) {
    const companyApi = new CompanyApi()
    await companyApi.updateCompany(data.id, data)
  }

  @Action
  async editCompanyInfo({companyId, data} : {companyId: string, data: Record<string, any>}) {
    if (!data.get('latitude')) {
      data.delete('latitude');
    }

    if (!data.get('longitude')) {
      data.delete('longitude');
    }
    const companyApi = new CompanyApi()
    await companyApi.updateCompanyInfo(companyId, data)
  }

}
